// Client-side sync engine — counterpart to backend/app/Domain/Sync.
//
// Responsibilities (spec §6.7, §17):
//   1. Drain pending_ops into the server in FIFO order, capped at 100 ops /
//      2 MB per batch.
//   2. Idempotency: server dedupes on operation uuid. We don't remove the
//      pending row until the server acks (accepted OR explicit reject).
//   3. Backoff: exponential, 1s → 5min; respect server's Retry-After header.
//   4. Conflict handling: persist into the local conflicts table so the
//      branch manager UI can surface them.
//   5. Single-flight: only one sync runs per device at a time.

import { api } from '@/lib/api';
import { db, type PendingOperation } from '@/db';
import { useSyncStore } from '@/stores/syncStore';
import { v4 as uuid } from 'uuid';

const MAX_OPS_PER_BATCH = 100;
const MAX_BYTES_PER_BATCH = 2 * 1024 * 1024;
const MAX_ATTEMPTS = 10;

// Validate UUID v1-5 — same regex the backend Laravel `uuid` validation accepts.
const UUID_V_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Drop pending ops whose uuid isn't a valid v1-5 — these can never be accepted
 * by the server (its validation rejects the whole batch on a single bad uuid).
 * This recovers from the "${orderUuid}-trigger" detritus introduced by a
 * prior version of submitOrderOffline before the bug was found.
 */
async function purgeInvalidUuidOps(): Promise<void> {
  const all = await db.pending_ops.toArray();
  const bad = all.filter((op) => !UUID_V_RE.test(op.uuid));
  if (bad.length === 0) return;
  await db.pending_ops.bulkDelete(bad.map((op) => op.id!).filter(Boolean));
  console.warn(`[sync] purged ${bad.length} legacy ops with invalid uuids`);
}

let inflight: Promise<void> | null = null;

export async function queueOperation(op: Omit<PendingOperation, 'id' | 'attempt_count'>): Promise<void> {
  await db.pending_ops.add({ ...op, attempt_count: 0 });
  useSyncStore.getState().bumpPending();

  // Try to flush opportunistically — silent if offline; SW will retry.
  if (navigator.onLine) {
    runSync().catch(() => { /* swallow, retried by schedule */ });
  }
}

export async function runSync(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      await syncOnce();
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

async function syncOnce(): Promise<void> {
  const deviceId = (await db.settings.get('device_id'))?.value as string | undefined;
  const branchId = (await db.settings.get('branch_id'))?.value as string | undefined;
  if (!deviceId || !branchId) return;

  useSyncStore.getState().setSyncing(true);

  try {
    // Recover from any legacy bad ops left behind in IndexedDB before
    // touching the network.
    await purgeInvalidUuidOps();

    // Pull reference data first to avoid placing orders against stale prices.
    await pullReferenceData(branchId);
    await pushPending(deviceId, branchId);
  } finally {
    useSyncStore.getState().setSyncing(false);
    useSyncStore.getState().setLastSyncedAt(new Date().toISOString());
  }
}

async function pullReferenceData(branchId: string): Promise<void> {
  // Menus are per-branch — if the device just switched branches, the cached
  // menu in Dexie belongs to a different one and would leak across.
  const lastBranch = (await db.settings.get('last_synced_branch_id'))?.value as string | undefined;
  const branchChanged = lastBranch !== branchId;

  // On a branch switch (or first sync), grab the full menu; otherwise
  // just the delta since the previous sync. The network call happens
  // BEFORE we touch Dexie so the POS keeps the current menu visible
  // throughout — no flash of empty grid while the request is in flight.
  // If the request fails we early-return below and the old menu stays
  // intact.
  const since = branchChanged
    ? '1970-01-01T00:00:00Z'
    : ((await db.settings.get('last_pull_at'))?.value as string | undefined ?? '1970-01-01T00:00:00Z');

  let res;
  try {
    res = await api.get('/sync/pull', { params: { since, branch_id: branchId } });
  } catch (e) {
    // Network blip or 5xx — leave the existing cache untouched. The next
    // sync tick will try again. Better a slightly stale menu than an
    // empty one.
    console.warn('[sync] menu pull failed; keeping existing cache', e);
    return;
  }
  const { menu_categories, menu_items } = res.data.data;

  // Partition incoming rows into tombstones (soft-deleted rows the server
  // sent so the client can drop them from cache) vs live upserts. This
  // is the fix for 'deleted menu items still appear in POS'.
  type Tombstoneable = { id: string; deleted_at?: string | null };
  const isAlive = (r: Tombstoneable) => !r.deleted_at;
  const liveCats:  Tombstoneable[] = (menu_categories ?? []).filter(isAlive);
  const deadCats:  Tombstoneable[] = (menu_categories ?? []).filter((r: Tombstoneable) => !isAlive(r));
  const liveItems: Tombstoneable[] = (menu_items ?? []).filter(isAlive);
  const deadItems: Tombstoneable[] = (menu_items ?? []).filter((r: Tombstoneable) => !isAlive(r));

  await db.transaction('rw', db.menu_categories, db.menu_items, async () => {
    if (branchChanged) {
      // Clear-and-replace in the SAME transaction so the live menu query
      // never sees an intermediate empty state. Dexie's useLiveQuery
      // observers fire once per transaction commit, not per write.
      await db.menu_categories.clear();
      await db.menu_items.clear();
    }
    if (liveCats.length)  await db.menu_categories.bulkPut(liveCats as never);
    if (liveItems.length) await db.menu_items.bulkPut(liveItems as never);
    if (deadCats.length)  await db.menu_categories.bulkDelete(deadCats.map((r) => r.id));
    if (deadItems.length) await db.menu_items.bulkDelete(deadItems.map((r) => r.id));
  });

  await db.settings.put({
    key: 'last_pull_at',
    value: res.data.meta.server_clock,
    updated_at: new Date().toISOString(),
  });
  await db.settings.put({
    key: 'last_synced_branch_id',
    value: branchId,
    updated_at: new Date().toISOString(),
  });
}

async function pushPending(deviceId: string, branchId: string): Promise<void> {
  const now = new Date();
  const ready = await db.pending_ops
    .filter((op) => {
      if (op.attempt_count >= MAX_ATTEMPTS) return false;
      if (op.next_retry_at && new Date(op.next_retry_at) > now) return false;
      return true;
    })
    .limit(MAX_OPS_PER_BATCH)
    .toArray();

  if (ready.length === 0) return;

  const operations = trimToByteLimit(ready);
  const batchId = uuid();

  try {
    const res = await api.post('/sync/push', {
      device_id:    deviceId,
      branch_id:    branchId,
      client_clock: new Date().toISOString(),
      operations:   operations.map(stripLocalFields),
    }, {
      headers: { 'X-Batch-Id': batchId, 'X-Idempotency-Key': batchId },
    });

    const { accepted = [], rejected = [], conflicts = [] } = res.data.data;

    await db.transaction('rw', db.pending_ops, db.conflicts, db.orders, db.payments, async () => {
      for (const a of accepted) {
        const op = operations.find((o) => o.uuid === a.uuid);
        if (op) await db.pending_ops.where('uuid').equals(op.uuid).delete();

        // Reflect canonical server status onto local rows.
        await db.orders.where('uuid').equals(a.uuid).modify({ sync_status: 'synced' });
        await db.payments.where('uuid').equals(a.uuid).modify({ sync_status: 'synced' });
      }
      for (const c of conflicts) {
        await db.conflicts.add({
          operation_uuid: c.uuid,
          entity_type: operations.find((o) => o.uuid === c.uuid)?.type ?? 'unknown',
          code: c.code,
          details: c.details,
          created_at: new Date().toISOString(),
        });
        await db.pending_ops.where('uuid').equals(c.uuid).modify({ last_error: c.code });
        await db.orders.where('uuid').equals(c.uuid).modify({ sync_status: 'conflict' });
      }
      for (const r of rejected) {
        await scheduleRetry(r.uuid, r.reason);
      }
    });
  } catch (err: unknown) {
    // Distinguish permanent (4xx, validation) from transient (5xx, network).
    // Permanent → drop the offending ops + record a conflict so a human can
    // see them. Transient → exponential backoff retry.
    const status = (err as { response?: { status?: number } })?.response?.status ?? 0;
    const isPermanent = status >= 400 && status < 500;

    if (isPermanent) {
      console.warn(`[sync] batch rejected (HTTP ${status}); marking ops as failed:`,
        operations.map((o) => o.uuid));
      await db.transaction('rw', db.pending_ops, db.conflicts, async () => {
        for (const op of operations) {
          await db.pending_ops.where('uuid').equals(op.uuid).delete();
          await db.conflicts.add({
            operation_uuid: op.uuid,
            entity_type:    op.type,
            code:           `HTTP_${status}`,
            details:        errorMessage(err),
            created_at:     new Date().toISOString(),
          });
        }
      });
    } else {
      for (const op of operations) {
        await scheduleRetry(op.uuid, errorMessage(err));
      }
    }
  }
}

function trimToByteLimit(ops: PendingOperation[]): PendingOperation[] {
  const out: PendingOperation[] = [];
  let size = 0;
  for (const op of ops) {
    const opSize = new Blob([JSON.stringify(op)]).size;
    if (size + opSize > MAX_BYTES_PER_BATCH) break;
    size += opSize;
    out.push(op);
  }
  return out.length ? out : ops.slice(0, 1); // ensure forward progress
}

function stripLocalFields(op: PendingOperation) {
  return {
    uuid: op.uuid,
    type: op.type,
    occurred_at: op.occurred_at,
    payload: op.payload,
  };
}

async function scheduleRetry(opUuid: string, reason: string): Promise<void> {
  const op = await db.pending_ops.where('uuid').equals(opUuid).first();
  if (!op) return;

  const attempts = op.attempt_count + 1;
  // 1, 2, 4, 8, 16... seconds capped at 300s
  const backoffSec = Math.min(2 ** (attempts - 1), 300);
  const nextRetryAt = new Date(Date.now() + backoffSec * 1000).toISOString();

  await db.pending_ops.update(op.id!, {
    attempt_count: attempts,
    next_retry_at: nextRetryAt,
    last_error: reason,
  });
}

function errorMessage(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message);
  return 'unknown_error';
}

// ─────────────────────────────────────────────────────────────────────────────
// Lifecycle hooks — register in main.tsx
// ─────────────────────────────────────────────────────────────────────────────

let intervalHandle: ReturnType<typeof setInterval> | null = null;

export function startSyncLoop(): void {
  // Periodic ticker — spec §6.7, every 30s.
  intervalHandle = setInterval(() => {
    if (navigator.onLine) runSync().catch(() => {});
  }, 30_000);

  window.addEventListener('online', () => runSync().catch(() => {}));
}

export function stopSyncLoop(): void {
  if (intervalHandle) clearInterval(intervalHandle);
  intervalHandle = null;
}
